import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { MaskedLine, FadeUp, ParallaxImage } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { IMAGES, usePageTitle } from "@/lib/site";

const CHAPTERS = [
  {
    id: "accounting",
    num: "01",
    title: "Accounting",
    intro:
      "Reliable accounting is the foundation every decision stands on. We run your accounting function — or strengthen the one you have — with disciplined closes and reporting you can defend.",
    points: [
      "Full-charge accounting and month-end close",
      "Financial statement preparation for owners, boards, and councils",
      "Grant and fund accounting for tribal governments",
      "Payroll coordination and sales tax filings",
      "Budget support and cash-flow visibility",
    ],
    image: IMAGES.accountants,
    alt: "Two professionals reviewing financial documents",
    testId: "chapter-accounting",
  },
  {
    id: "audit-preparation",
    num: "02",
    title: "Audit Preparation",
    intro:
      "An audit should confirm your discipline, not expose your stress. We prepare your organization so fieldwork is routine — including Uniform Guidance single audits for tribal governments and federal award programs.",
    points: [
      "Audit-ready workpapers and lead schedules",
      "PBC list management and auditor liaison",
      "Account reconciliations and supporting schedules",
      "Internal control review and remediation support",
      "Single audit (Uniform Guidance) readiness",
    ],
    image: IMAGES.calculator,
    alt: "Calculator and financial documents close-up",
    testId: "chapter-audit-preparation",
  },
  {
    id: "bookkeeping",
    num: "03",
    title: "Bookkeeping",
    intro:
      "Clean books, kept current, quietly. We handle the day-to-day recording so your reports are always ready — for a lender, a board meeting, or tax season.",
    points: [
      "Monthly bookkeeping and categorization",
      "Accounts payable and receivable management",
      "Bank and credit card reconciliations",
      "QuickBooks setup, clean-up, and catch-up",
      "Monthly reporting packages in plain language",
    ],
    image: IMAGES.office,
    alt: "Organized modern desk",
    testId: "chapter-bookkeeping",
  },
];

const PROCESS = [
  { num: "1", title: "Discover", desc: "A short conversation about where your books stand and where they need to be." },
  { num: "2", title: "Design", desc: "A scoped engagement with clear deliverables, cadence, and a fixed monthly fee." },
  { num: "3", title: "Deliver", desc: "Consistent closes, reports, and audit support — on time, every cycle." },
  { num: "4", title: "Review", desc: "Regular check-ins to refine the work as your organization grows." },
];

export default function Services() {
  usePageTitle("Services — Accounting, Audit Preparation & Bookkeeping | CalCor Solutions");
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 350);
    }
  }, [hash]);

  return (
    <>
      <section data-testid="services-hero" className="pt-36 pb-16 sm:pt-44 lg:pb-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <MaskedLine delay={0.15} className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            Services
          </MaskedLine>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.3}>Every number,</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-pine">accounted for.</em>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              Three practices, one team, one standard. Each engagement is scoped to your
              organization — whether you are a growing small business or a tribal government
              preparing for a single audit.
            </p>
          </FadeUp>
        </div>
      </section>

      {CHAPTERS.map((c, i) => (
        <section
          key={c.id}
          id={c.id}
          data-testid={c.testId}
          className="border-t border-black/10 py-24 lg:py-32"
        >
          <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10">
            <div className={i % 2 === 1 ? "lg:order-2 lg:col-span-6" : "lg:col-span-6"}>
              <FadeUp>
                <span className="font-serif text-7xl italic leading-none text-pine/20 sm:text-8xl">
                  {c.num}
                </span>
                <h2 className="-mt-6 font-serif text-4xl tracking-tighter text-ink sm:-mt-8 sm:text-5xl">
                  {c.title}
                </h2>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg">
                  {c.intro}
                </p>
                <ul className="mt-8 space-y-3.5">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-zinc-700 sm:text-base">
                      <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pine/10">
                        <Check className="h-3 w-3 text-pine" />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/contact"
                  data-testid={`${c.id}-cta-button`}
                  className="group mt-10 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
                >
                  Discuss {c.title.toLowerCase()}
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                {c.id === "accounting" && (
                  <Link
                    to="/services/accounting"
                    data-testid="accounting-page-link"
                    className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
                  >
                    See the accounting services page
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
                {c.id === "audit-preparation" && (
                  <Link
                    to="/services/audit-preparation"
                    data-testid="audit-prep-page-link"
                    className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
                  >
                    See the audit preparation page
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                )}
                {c.id === "bookkeeping" && (
                  <>
                    <Link
                      to="/services/monthly-bookkeeping"
                      data-testid="bookkeeping-plans-link"
                      className="group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
                    >
                      See the monthly bookkeeping page
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                    <Link
                      to="/services/quickbooks-cleanup"
                      data-testid="quickbooks-cleanup-link"
                      className="group mt-3 inline-flex items-center gap-2 text-sm font-semibold text-pine transition-colors hover:text-clay"
                    >
                      QuickBooks cleanup & catch-up page
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                  </>
                )}
              </FadeUp>
            </div>
            <div className={i % 2 === 1 ? "lg:order-1 lg:col-span-6" : "lg:col-span-6"}>
              <FadeUp delay={0.15}>
                <ParallaxImage
                  src={c.image}
                  alt={c.alt}
                  testId={`${c.id}-image`}
                  arch={i === 1}
                  className={i === 1 ? "aspect-[4/5] w-full" : "aspect-[4/3] w-full rounded-3xl"}
                />
              </FadeUp>
            </div>
          </div>
        </section>
      ))}

      <Marquee
        dark
        items={["Month-End Closes", "Single Audit Readiness", "QuickBooks Clean-Up", "Board Reporting", "PBC Coordination"]}
      />

      {/* ————— Process ————— */}
      <section data-testid="process-section" className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-pine">
              <span className="h-px w-10 bg-pine" />
              How we work
            </p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl leading-tight tracking-tighter text-ink sm:text-5xl">
              A calm, predictable <em className="text-pine">process.</em>
            </h2>
          </FadeUp>
          <div className="mt-16 grid gap-px overflow-hidden rounded-3xl border border-black/10 bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
            {PROCESS.map((s, i) => (
              <FadeUp key={s.num} delay={i * 0.1} className="bg-white">
                <div data-testid={`process-step-${s.num}`} className="h-full p-9 transition-colors duration-300 hover:bg-paper">
                  <span className="font-serif text-4xl italic text-pine/30">{s.num}</span>
                  <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-600">{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* ————— CTA ————— */}
      <section data-testid="services-cta" className="bg-pine py-24 text-paper">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <h2 className="max-w-3xl font-serif text-4xl leading-tight tracking-tighter sm:text-5xl">
              Not sure which service fits? <em className="text-paper/70">Let&rsquo;s talk it through.</em>
            </h2>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="services-cta-contact-button"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-semibold text-pine transition-colors duration-300 hover:bg-clay hover:text-white"
              >
                Request a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact#booking"
                data-testid="services-cta-booking-button"
                className="group inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-4 text-sm font-semibold text-paper transition-colors duration-300 hover:border-white hover:bg-white/10"
              >
                Book an appointment
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
